import { inject, Injectable } from "@angular/core";
import { VisitsService } from "../../../api/services";
import { environment } from "../../../environments/environment";
import {
  CreateVisitDto,
  FilterVisitDto,
  ResponseVisitDto,
  UpdateVisitDto,
} from "../../../api/models";
import { Observable } from "rxjs";

const TENANT = environment.tenant;

@Injectable({
  providedIn: "root",
})
export class VisitsApiService {
  /** The visits service */
  private readonly _visitsService: VisitsService = inject(VisitsService);

  /**
   * Creates a new visit.
   * @param visit The visit to create.
   * @returns The created visit.
   */
  public create$(body: CreateVisitDto): Observable<ResponseVisitDto> {
    return this._visitsService.visitsControllerCreate({ body });
  }

  /**
   * Updates a visit.
   * @param id The id of the visit to update.
   * @param body The visit to update.
   * @returns The updated visit.
   */
  public update$(
    id: string,
    body: UpdateVisitDto,
  ): Observable<ResponseVisitDto> {
    return this._visitsService.visitsControllerUpdate({
      tenant: TENANT,
      id,
      body,
    });
  }

  /**
   * Deletes a visit.
   * @param id The id of the visit to delete.
   * @returns The deleted visit.
   */
  public delete$(id: string): Observable<void> {
    return this._visitsService.visitsControllerDelete({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Retrieves a visit.
   * @param id The id of the visit to retrieve.
   * @returns The retrieved visit.
   */
  public findOne$(id: string): Observable<ResponseVisitDto> {
    return this._visitsService.visitsControllerFindOne({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Filters visits.
   * @param filterDto The filter DTO.
   * @returns The filtered visits.
   */
  public filter$(
    filterDto: Partial<FilterVisitDto>,
  ): Observable<ResponseVisitDto[]> {
    const body: FilterVisitDto = {
      ...filterDto,
      tenant: TENANT,
    };

    return this._visitsService.visitsControllerFilter({
      body,
    });
  }
}
